import "@/App.css";
import { useState } from "react";
import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Dashboard from "@/pages/Dashboard";
import Scan from "@/pages/Scan";
import Inventory from "@/pages/Inventory";
import PackagingLists from "@/pages/PackagingLists";
import Consume from "@/pages/Consume";
import SlittingDashboard from "@/pages/SlittingDashboard";
import Dispatch from "@/pages/Dispatch";
import ProductionFloor from "@/pages/ProductionFloor";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import PendingApproval from "@/pages/PendingApproval";
import Users from "@/pages/Users";
import Masters from "@/pages/Masters";
import Orders from "@/pages/Orders";
import ProductionPlanning from "@/pages/ProductionPlanning";
import FG from "@/pages/FG";
import Performance from "@/pages/Performance";
import HR from "@/pages/HR";
import DataSafety from "@/pages/DataSafety";
import Store from "@/pages/Store";
import CRM from "@/pages/CRM";
import CRMLead from "@/pages/CRMLead";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, Shield, LogOut, Loader2, Truck, Hammer, Database,
  Tags, PackagePlus, Scissors, Boxes, ClipboardList, Warehouse, Menu, Calendar,
  Users as UsersIcon, Briefcase, ShieldCheck, PackageSearch, Handshake,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const ROLE_LABELS = {
  director: "Director",
  dispatch_executive: "Dispatch",
  slitting_operator: "Accounts",
  press_operator: "Production",
  store_manager: "Store Manager",
};

const NAV = [
  { to: "/", end: true, label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard", module: "dashboard" },
  { to: "/packaging-lists", label: "RM/SFG Coil Tags", icon: Tags, testId: "nav-packaging", module: "rm_coil_tags" },
  { to: "/scan", label: "RM/SFG Inward", icon: PackagePlus, testId: "nav-scan", module: "rm_inward" },
  { to: "/consume", label: "Slitting Operations", icon: Scissors, testId: "nav-consume", module: "slitting_ops" },
  { to: "/production", label: "Slit Coil Usage", icon: Hammer, testId: "nav-production", module: "slit_coil_usage" },
  { to: "/fg", label: "FG Production", icon: Boxes, testId: "nav-fg", module: "fg_production" },
  { to: "/orders", label: "Sales Orders", icon: ClipboardList, testId: "nav-orders", module: "sales_orders" },
  { to: "/planning", label: "Production Planning", icon: Calendar, testId: "nav-planning", module: "production_plan" },
  { to: "/dispatch", label: "Dispatch & Logistics", icon: Truck, testId: "nav-dispatch", module: "dispatch" },
  { to: "/inventory", label: "Inventory", icon: Warehouse, testId: "nav-inventory", module: "inventory" },
  { to: "/store", label: "Store (Non-Steel)", icon: PackageSearch, testId: "nav-store", module: "store" },
  { to: "/crm", label: "CRM", icon: Handshake, testId: "nav-crm", module: "crm" },
  { to: "/hr", label: "Human Resources", icon: Briefcase, testId: "nav-hr", module: "hr" },
  { to: "/users", label: "ERP Users", icon: Shield, testId: "nav-users", module: "erp_users" },
  { to: "/masters", label: "Masters", icon: Database, testId: "nav-masters", module: "masters" },
  { to: "/data-safety", label: "Data Safety", icon: ShieldCheck, testId: "nav-data-safety", module: "data_safety" },
];

const SideNavItem = ({ to, end, icon: Icon, label, testId, onNavigate }) => (
  <NavLink
    to={to}
    end={end}
    data-testid={testId}
    onClick={onNavigate}
    className={({ isActive }) =>
      `flex items-center gap-3 px-4 py-3 text-sm font-medium tracking-wide transition-colors border-l-2 ${
        isActive
          ? "border-slate-900 bg-slate-100 text-slate-900"
          : "border-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-50"
      }`
    }
  >
    <Icon className="h-4 w-4 shrink-0" />
    <span>{label}</span>
    {to === "/fg" && <FgReviewBadge />}
  </NavLink>
);

// Count of FG pallets/lines waiting for review (floor scale) — shown on the FG menu item.
const FgReviewBadge = () => {
  const { data } = useQuery({ queryKey: ["fg-scale", "status"], queryFn: async () => (await api.get("/fg-scale/status")).data, refetchInterval: 30_000 });
  if (!data?.needs_review) return null;
  return <span data-testid="nav-fg-review-badge" className="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-400 text-amber-950">{data.needs_review}</span>;
};

function Header() {
  const { user, logout, hasModule } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const allowed = NAV.filter((n) => hasModule(n.module));

  return (
    <header data-testid="app-header" className="sticky top-0 z-50 bg-white border-b border-slate-200">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 flex items-center justify-between h-16 gap-4">
        <div className="flex items-center gap-3 flex-shrink-0">
          <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                data-testid="open-nav-drawer"
                aria-label="Open navigation"
                className="rounded-sm h-9 w-9"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72 sm:w-80 flex flex-col">
              <SheetHeader className="px-4 py-4 border-b border-slate-200 bg-slate-900 text-white shrink-0">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 bg-white flex items-center justify-center rounded-sm p-0.5 shrink-0">
                    <img src="/sre-logo.webp" alt="SR Electrosteel" className="h-full w-full object-contain" />
                  </div>
                  <div className="text-left min-w-0">
                    <p className="text-[9px] tracking-[0.22em] uppercase text-slate-400 font-semibold leading-none">Enterprise Resource Planning</p>
                    <SheetTitle className="text-sm font-bold tracking-tight text-white leading-tight font-heading mt-1 truncate">
                      SR ELECTROSTEEL PRIVATE LIMITED
                    </SheetTitle>
                  </div>
                </div>
              </SheetHeader>
              <nav data-testid="side-nav" className="flex-1 overflow-y-auto py-2">
                {allowed.map((n) => (
                  <SideNavItem
                    key={n.to}
                    {...n}
                    onNavigate={() => setDrawerOpen(false)}
                  />
                ))}
              </nav>
              <div className="border-t border-slate-200 px-4 py-3 flex items-center justify-between gap-2 shrink-0">
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-slate-900 truncate">{user?.name}</p>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">{ROLE_LABELS[user?.role] || user?.role}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setDrawerOpen(false); logout(); }}
                  data-testid="drawer-logout-btn"
                  className="h-8"
                >
                  <LogOut className="h-3.5 w-3.5 mr-1.5" /> Sign out
                </Button>
              </div>
            </SheetContent>
          </Sheet>
          <div className="h-10 w-10 bg-white border border-slate-200 flex items-center justify-center rounded-sm p-0.5">
            <img src="/sre-logo.webp" alt="SR Electrosteel" className="h-full w-full object-contain" />
          </div>
          <div className="hidden md:block">
            <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold leading-none">Enterprise Resource Planning</p>
            <h1 className="text-sm font-bold tracking-tight text-slate-900 leading-tight font-heading mt-1">SR ELECTROSTEEL PRIVATE LIMITED</h1>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="hidden lg:block text-right">
            <p className="text-xs font-semibold text-slate-900 leading-tight" data-testid="user-name">{user?.name}</p>
            <p className="text-[10px] uppercase tracking-wider text-slate-500" data-testid="user-role">{ROLE_LABELS[user?.role] || user?.role}</p>
          </div>
          {user?.picture ? (
            <img src={user.picture} alt="avatar" className="h-8 w-8 rounded-full object-cover border border-slate-200" />
          ) : (
            <div className="h-8 w-8 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center text-xs font-bold">
              {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-btn" title="Sign out" className="rounded-sm h-9 w-9">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </header>
  );
}

const ModuleRoute = ({ module, children }) => {
  const { user, hasModule } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "pending") return <Navigate to="/pending" replace />;
  if (!hasModule(module)) return <Navigate to="/" replace />;
  return children;
};

function RoleHomeRedirect() {
  const { user, hasModule } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "pending") return <Navigate to="/pending" replace />;
  // If the user has dashboard access, land there; otherwise send them to their
  // first accessible module in NAV order.
  if (hasModule("dashboard")) return <Dashboard />;
  const first = NAV.find((n) => hasModule(n.module));
  if (first && first.to !== "/") return <Navigate to={first.to} replace />;
  return <Dashboard />;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Auth callback handling — process session_id in URL fragment BEFORE checking auth state
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  // Login / Pending screens have NO header
  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  if (user.role === "pending") {
    return (
      <Routes>
        <Route path="/pending" element={<PendingApproval />} />
        <Route path="*" element={<Navigate to="/pending" replace />} />
      </Routes>
    );
  }

  // Authenticated + active
  return (
    <>
      <Header />
      <main className={`${location.pathname === "/scan" ? "max-w-none" : "max-w-[1400px]"} mx-auto px-4 sm:px-6 lg:px-10 py-8`}>
        <Routes>
          <Route path="/" element={<RoleHomeRedirect />} />
          <Route path="/packaging-lists" element={<ModuleRoute module="rm_coil_tags"><PackagingLists /></ModuleRoute>} />
          <Route path="/scan" element={<ModuleRoute module="rm_inward"><Scan /></ModuleRoute>} />
          <Route path="/consume" element={<ModuleRoute module="slitting_ops"><SlittingDashboard /></ModuleRoute>} />
          <Route path="/consume/legacy" element={<ModuleRoute module="slitting_ops"><Consume /></ModuleRoute>} />
          <Route path="/production" element={<ModuleRoute module="slit_coil_usage"><ProductionFloor /></ModuleRoute>} />
          <Route path="/fg" element={<ModuleRoute module="fg_production"><FG /></ModuleRoute>} />
          <Route path="/orders" element={<ModuleRoute module="sales_orders"><Orders /></ModuleRoute>} />
          <Route path="/planning" element={<ModuleRoute module="production_plan"><ProductionPlanning /></ModuleRoute>} />
          <Route path="/dispatch" element={<ModuleRoute module="dispatch"><Dispatch /></ModuleRoute>} />
          <Route path="/inventory" element={<ModuleRoute module="inventory"><Inventory /></ModuleRoute>} />
          <Route path="/store" element={<ModuleRoute module="store"><Store /></ModuleRoute>} />
          <Route path="/crm" element={<ModuleRoute module="crm"><CRM /></ModuleRoute>} />
          <Route path="/crm/:id" element={<ModuleRoute module="crm"><CRMLead /></ModuleRoute>} />
          <Route path="/hr" element={<ModuleRoute module="hr"><HR /></ModuleRoute>} />
          <Route path="/performance" element={<ModuleRoute module="performance"><Performance /></ModuleRoute>} />
          <Route path="/users" element={<ModuleRoute module="erp_users"><Users /></ModuleRoute>} />
          <Route path="/masters" element={<ModuleRoute module="masters"><Masters /></ModuleRoute>} />
          <Route path="/data-safety" element={<ModuleRoute module="data_safety"><DataSafety /></ModuleRoute>} />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-slate-50 text-slate-900 font-body">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
